import type { ComponentType } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Scale,
  BarChart2,
  Building2,
  Users,
  Settings,
  LogOut,
  Ticket
} from 'lucide-react';
import { useAuth } from '../../app/hooks/useAuth';
import { buildAppPath, AppRouteKey } from '../../utils/navigation';
import { RoleBadge } from '../ui/pesage';
import { PesageButton } from '../ui/pesage';
import type { UserRole } from '../../entities/user/types';

interface NavItem {
  key: AppRouteKey;
  label: string;
  icon: ComponentType<{ size?: number; className?: string }>;
  roles: UserRole[];
}

interface SidebarProps {
  onNavigate?: () => void;
}

const navItems: NavItem[] = [
  { key: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard, roles: ['manager'] },
  { key: 'weighing', label: 'Saisie de Pesage', icon: Scale, roles: ['agent'] },
  { key: 'tickets', label: 'Tickets', icon: Ticket, roles: ['manager', 'agent', 'accounting'] },
  { key: 'reports', label: 'Rapports', icon: BarChart2, roles: ['manager', 'accounting'] },
  { key: 'configuration', label: 'Configuration', icon: Settings, roles: ['manager'] },
  { key: 'companies', label: 'Sociétés', icon: Building2, roles: ['super_admin'] },
  { key: 'users', label: 'Utilisateurs', icon: Users, roles: ['super_admin'] },
];

const roleMapping: Record<UserRole, 'MANAGER' | 'AGENT' | 'COMPTABLE' | 'SUPER ADMIN'> = {
  manager: 'MANAGER',
  agent: 'AGENT',
  accounting: 'COMPTABLE',
  super_admin: 'SUPER ADMIN',
};

export function Sidebar({ onNavigate }: SidebarProps) {
  const { authState, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const userRole = authState.user?.role;

  const filteredNavItems = userRole
    ? navItems.filter((item) => item.roles.includes(userRole))
    : [];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (firstName: string, lastName: string) =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  return (
    <div className="flex h-full w-[240px] flex-col border-r border-[#334155] bg-[#1e293b]">
      <div className="border-b border-[#334155] px-4 py-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-emerald-500/20">
            <Scale className="text-emerald-400" size={20} />
          </div>
          <div className="min-w-0">
            <p className="truncate text-lg font-bold tracking-tight text-slate-100">
              Fathy Pesage
            </p>
            <p className="truncate text-[10px] uppercase tracking-[0.28em] text-emerald-500">
              Gestion &amp; Facturation
            </p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-5">
        {filteredNavItems.map((item) => {
          const itemPath = buildAppPath(item.key, authState);
          const isActive =
            currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
          const Icon = item.icon;

          return (
            <NavLink
              key={item.key}
              to={itemPath}
              onClick={onNavigate}
              className={`mx-0 flex items-center gap-3 px-4 py-3 text-sm transition-all ${
                isActive
                  ? 'border-l-[3px] border-emerald-500 bg-[#0f2318] pl-[13px] font-medium text-emerald-400'
                  : 'border-l-[3px] border-transparent text-slate-400 hover:bg-[#263548] hover:text-slate-100'
              }`}
            >
              <Icon size={18} className="shrink-0" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="mt-auto border-t border-[#334155] px-4 py-4">
        {authState.user && (
          <div className="mb-4">
            <div className="mb-2 flex items-center gap-3 px-2 py-2 text-slate-400">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#263548]">
                <span className="text-xs font-bold text-slate-300">
                  {getInitials(authState.user.firstName, authState.user.lastName)}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-100">
                  {authState.user.firstName} {authState.user.lastName}
                </p>
                {userRole && (
                  <RoleBadge role={roleMapping[userRole]} className="mt-1 border-none bg-transparent p-0 text-[10px] tracking-[0.24em] text-slate-400" />
                )}
              </div>
            </div>
          </div>
        )}
        <PesageButton
          variant="ghost"
          onClick={() => {
            void handleLogout();
            onNavigate?.();
          }}
          className="w-full justify-start rounded-lg border-none px-2 py-3 text-slate-400 hover:bg-transparent hover:text-slate-100"
          size="sm"
          icon={LogOut}
        >
          Déconnexion
        </PesageButton>
      </div>
    </div>
  );
}
